import { yupResolver } from '@hookform/resolvers/yup';
import { PersonAddOutlined } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React, { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import MenuItem from '@mui/material/MenuItem';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { ApiClient } from '../../lib/api/client';
import { setToken } from '../../lib/auth';
import { RootState } from '../../store';
import { useRegisterMutation } from '../../store/api/authApi';
import { setCredentials } from '../../store/slices/authSlice';
import { setTenant } from '../../store/slices/tenantSlice';
import extractApiError from '../../utils/extractApiError';
import { formatRemainingTime, useRegistrationRateLimit } from '../../utils/rateLimiter';

import PasswordField from '@/components/forms/PasswordField';
import ShrinkTextField from '@/components/forms/ShrinkTextField';

// Keep cookie name aligned across app & harness. Other flows set a 'token' cookie,
// and the smoke harness expects a JWT-looking cookie immediately after auth.
// (See withAuth/login flows that also set 'token'.)
const AUTH_COOKIE = 'token';

const schema = yup.object({
  companyName: yup.string().required('Company name is required'),
  companyDescription: yup
    .string()
    .min(50, 'Description must be at least 50 characters')
    .max(500, 'Description must be less than 500 characters')
    .required('Company description is required'),
  subdomain: yup.string().required('Workspace URL is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match'),
  termsAccepted: yup
    .boolean()
    .oneOf([true], 'You must accept the terms and conditions')
    .required('You must accept the terms and conditions'),
});

/**
 * E2E/Week-11 stable registration form (no RHF/MUI focus churn).
 * Used only when running in Week-11 harness mode.
 */
const W11Register: React.FC = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  // Duplicate-submit guard (button disable + ref—covers rapid Enter/spam)
  const submittingRef = React.useRef(false);
  const [companyName, setCompanyName] = React.useState('');
  const [companyDescription, setCompanyDescription] = React.useState('');
  const [subdomain, setSubdomain] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [localErr, setLocalErr] = React.useState<string | null>(null);

  async function submitStable(e: React.FormEvent) {
    e.preventDefault();
    if (busy || submittingRef.current) return; // hard guard
    setLocalErr(null);
    if (
      !companyName ||
      !companyDescription ||
      !subdomain ||
      !firstName ||
      !lastName ||
      !email ||
      !password ||
      !confirmPassword
    ) {
      setLocalErr('All fields are required, including company description');
      return;
    }
    if (companyDescription.length < 50 || companyDescription.length > 500) {
      setLocalErr('Company description must be 50-500 characters');
      return;
    }
    if (password !== confirmPassword) {
      setLocalErr('Passwords must match');
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    try {
      const canonicalSub = (subdomain || '').trim().toLowerCase();
      const data: any = await ApiClient.post('/auth/register', {
        company_name: companyName,
        subdomain: canonicalSub,
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        termsAccepted: true, // Validated by form schema before reaching here
      });
      const tok = data?.token ?? data?.accessToken ?? data?.jwt ?? data?.data?.token ?? '';
      if (!tok) throw new Error('No token returned');
      const tid = data?.user?.tenantId ?? data?.user?.tenant_id ?? '';
      // Persist minimal session (cookie + storage) and Redux sync
      try {
        // single place to keep cookie/localStorage in sync
        try {
          setToken(tok);
        } catch {
          /* ignore */
        }
        // Ensure immediate SSR/harness detection; mirror cookie name used elsewhere
        try {
          const secure =
            typeof window !== 'undefined' && window.location.protocol === 'https:'
              ? '; Secure'
              : '';
          document.cookie = `${AUTH_COOKIE}=${encodeURIComponent(tok)}; Path=/; SameSite=Lax; Max-Age=86400${secure}`;
        } catch {
          /* ignore */
        }
        localStorage.setItem('token', tok);
        if (tid) localStorage.setItem('tenantId', tid);
        localStorage.setItem('subdomain', canonicalSub);
        try {
          sessionStorage.setItem('subdomain', canonicalSub);
        } catch {
          /* ignore */
        }
      } catch {
        /* ignore */
      }
      try {
        dispatch(
          setCredentials({
            token: tok,
            user: data?.user ?? {},
            tenantId: tid || null,
            subdomain: canonicalSub,
          } as any),
        );
        dispatch(setTenant(canonicalSub));
      } catch {
        /* ignore */
      }
      await router.replace('/dashboard');
    } catch (err: any) {
      setLocalErr(err?.message || 'Registration failed');
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <PersonAddOutlined />
          </Avatar>

          <Typography component="h1" variant="h5">
            Create Account
          </Typography>
          {localErr && (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {localErr}
            </Alert>
          )}
          <Box component="form" onSubmit={submitStable} noValidate sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <label htmlFor="companyNameInput">Company Name</label>
                <input
                  id="companyNameInput"
                  name="companyName"
                  aria-label="Company Name"
                  placeholder="Company Name"
                  data-testid="companyName"
                  autoComplete="organization"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  style={{ width: '100%', padding: 12, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </Grid>
              <Grid item xs={12}>
                <label htmlFor="companyDescriptionInput">About Your Company</label>
                <textarea
                  id="companyDescriptionInput"
                  name="companyDescription"
                  aria-label="About Your Company"
                  placeholder="Tell customers about your company, services, and expertise (50-500 characters)"
                  data-testid="companyDescription"
                  rows={4}
                  value={companyDescription}
                  onChange={(e) => setCompanyDescription(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '1px solid #ccc',
                    borderRadius: 8,
                    resize: 'vertical',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <label htmlFor="subdomainInput">Workspace URL</label>
                <input
                  id="subdomainInput"
                  name="subdomain"
                  aria-label="Workspace URL"
                  placeholder="Workspace URL"
                  data-testid="subdomain"
                  autoComplete="off"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value)}
                  style={{ width: '100%', padding: 12, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <label htmlFor="firstNameInput">First Name</label>
                <input
                  id="firstNameInput"
                  name="firstName"
                  aria-label="First Name"
                  placeholder="First Name"
                  data-testid="firstName"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  style={{ width: '100%', padding: 12, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <label htmlFor="lastNameInput">Last Name</label>
                <input
                  id="lastNameInput"
                  name="lastName"
                  aria-label="Last Name"
                  placeholder="Last Name"
                  data-testid="lastName"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  style={{ width: '100%', padding: 12, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </Grid>
              <Grid item xs={12}>
                <label htmlFor="emailInput">Email Address</label>
                <input
                  id="emailInput"
                  name="email"
                  type="email"
                  aria-label="Email Address"
                  placeholder="Email Address"
                  data-testid="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ width: '100%', padding: 12, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </Grid>
              <Grid item xs={12}>
                <label htmlFor="passwordInput">Password</label>
                <input
                  id="passwordInput"
                  name="password"
                  aria-label="Password"
                  type="password"
                  placeholder="Password"
                  data-testid="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ width: '100%', padding: 12, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </Grid>
              <Grid item xs={12}>
                <label htmlFor="confirmPasswordInput">Confirm Password</label>
                <input
                  id="confirmPasswordInput"
                  name="confirmPassword"
                  aria-label="Confirm Password"
                  type="password"
                  placeholder="Confirm Password"
                  data-testid="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ width: '100%', padding: 12, border: '1px solid #ccc', borderRadius: 8 }}
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={busy}
            >
              {busy ? <CircularProgress size={24} /> : 'Sign Up'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={NextLink} href="/login" variant="body2">
                Already have an account? Sign in
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

/**
 * Full RHF/MUI implementation (production UX).
 * Kept separate so hooks are never called conditionally.
 */
const RegisterRHF: React.FC<{ catalogSlug?: string }> = ({ catalogSlug }) => {
  // WebDriver detection for E2E-only UX gates
  const IS_E2E = typeof navigator !== 'undefined' && (navigator as any).webdriver === true;
  // Enable via:  NEXT_PUBLIC_REGDBG=1
  const REGDBG =
    process.env.NEXT_PUBLIC_REGDBG === '1' ||
    process.env.NEXT_PUBLIC_DEBUG_E2E === 'true' ||
    (typeof window !== 'undefined' &&
      ((window as any).__hyp__?.regdbg === true || localStorage.getItem('REGDBG') === '1'));
  const tnow = () =>
    typeof performance !== 'undefined' && performance.now
      ? performance.now().toFixed(1)
      : Date.now();
  const log = React.useCallback(
    (...args: any[]) => {
      // Debug logging removed for production
    },
    [REGDBG],
  );

  const router = useRouter();
  const dispatch = useDispatch();
  const [, { isLoading, error }] = useRegisterMutation();
  // Duplicate-submit guard (persists across renders)
  const submitRef = useRef(false);

  // local error message we control (used for 409 duplicate email, etc.)
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // be defensive during hydration/SSR
  const savedSubdomain = useSelector((state: RootState) => state?.tenant?.subdomain ?? '');
  // FAANG-grade: avoid eager re-validation during typing (stabilizes DOM)
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    shouldUnregister: false,
    defaultValues: { subdomain: savedSubdomain || '' },
  });

  // Render counter
  const renderCount = useRef(0);
  renderCount.current++;
  log('Register render #', renderCount.current);

  // Value & error timelines (RHF/yup signal)
  const subdomainVal = watch('subdomain');
  const firstNameVal = watch('firstName');
  const emailVal = watch('email');
  React.useEffect(() => {
    log('subdomain ->', subdomainVal);
  }, [subdomainVal, log]); // H1 timeline
  React.useEffect(() => {
    log('firstName ->', firstNameVal);
  }, [firstNameVal, log]); // H1 timeline
  React.useEffect(() => {
    log('errors ->', Object.keys(errors || {}));
  }, [errors, log]); // H1 signal

  // Detect DOM detach/remount of subdomain & firstName (H1 smoking gun)
  React.useEffect(() => {
    if (!REGDBG) return;
    const sel1 = 'input[name="subdomain"]';
    const sel2 = 'input[name="firstName"]';
    let present1 = !!document.querySelector(sel1);
    let present2 = !!document.querySelector(sel2);
    const obs = new MutationObserver(() => {
      const now1 = !!document.querySelector(sel1);
      const now2 = !!document.querySelector(sel2);
      if (now1 !== present1) {
        present1 = now1;
        // Debug: subdomain attached/detached
      }
      if (now2 !== present2) {
        present2 = now2;
        // Debug: firstName attached/detached
      }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
    // note if it was auto-focused right after mount
    setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>(sel2);
      if (el && document.activeElement === el) {
        // Debug: firstName auto-focused at mount
      }
    }, 0);
    return () => obs.disconnect();
  }, [REGDBG]);

  // Wrap RHF register() so we can timestamp change/focus precisely
  const regTap = React.useCallback(
    (name: Parameters<typeof register>[0], opts?: Parameters<typeof register>[1]) => {
      const base = register(name as any, opts);
      return {
        ...base,
        onChange: (e: any) => {
          log('onChange', String(name), e?.target?.value);
          return base.onChange(e);
        },
        onBlur: (e: any) => {
          log('onBlur', String(name));
          return base.onBlur(e);
        },
        onFocus: (_e: any) => {
          log('onFocus', String(name));
        },
      };
    },
    [register, log],
  );

  const onSubmit = async (data: any) => {
    // Duplicate-submit guard (in addition to isLoading)
    if (isLoading || submitRef.current) return;
    submitRef.current = true;
    try {
      setSubmitError(null); // clear any previous local error

      const { confirmPassword, ...rest } = data;
      const canonicalSub = String(rest.subdomain || '')
        .trim()
        .toLowerCase();
      // Use the new partner admin registration endpoint
      const payload = {
        email: rest.email,
        password: rest.password,
        firstName: rest.firstName,
        lastName: rest.lastName,
        companyName: rest.companyName,
        companyDescription: rest.companyDescription,
        subdomain: canonicalSub,
        businessAddress: '', // Required field, using empty for now
        businessPhone: '', // Required field, using empty for now
        businessWebsite: null,
        industry: 'Technology', // Default industry
        companySize: '1-10', // Default company size
        termsAccepted: rest.termsAccepted,
      };

      // Use ApiClient to call the new partner admin endpoint
      const result: any = await ApiClient.post('/auth/register/partner-admin', payload);

      // Handle new response structure with brand/catalogInstance
      let token = result?.token ?? '';
      const user = result?.user ?? {};
      const brand = result?.brand ?? {};
      const catalogInstance = brand?.catalogInstance ?? {};

      // Extract tenant info from brand/catalogInstance
      const tenantId = brand?.id ?? catalogInstance?.id ?? '';
      const subdomain = catalogInstance?.subdomain ?? canonicalSub;

      // Fallback: if the register endpoint didn't return a token, try logging in once (hit the API host, not Next).
      if (!token) {
        try {
          const d = await ApiClient.login({
            email: rest.email,
            password: rest.password,
          });
          token = (d as any)?.token || '';
        } catch {
          /* ignore */
        }
      }

      // If we still don't have a token, hand off to /auth/login and let LoginForm auto-submit
      if (!token) {
        if (typeof window !== 'undefined') {
          localStorage.setItem('subdomain', canonicalSub || '');
        }
        dispatch(setTenant(rest.subdomain));
        const qs = new URLSearchParams({
          email: rest.email,
          password: rest.password,
          subdomain: canonicalSub ?? '',
        });
        await router.replace(`/login?${qs.toString()}`);
        return;
      }

      // We have a token: persist minimal auth context and go straight to dashboard.
      if (typeof window !== 'undefined') {
        setToken(token); // sets cookie + localStorage in one place
        // Ensure immediate SSR/harness detection; mirror cookie name used elsewhere
        try {
          const secure =
            typeof window !== 'undefined' && window.location.protocol === 'https:'
              ? '; Secure'
              : '';
          document.cookie = `${AUTH_COOKIE}=${encodeURIComponent(token)}; Path=/; SameSite=Lax; Max-Age=86400${secure}`;
        } catch {
          /* ignore */
        }
        if (tenantId) localStorage.setItem('tenantId', tenantId);
        localStorage.setItem('subdomain', subdomain || '');
        // Store user so withAuth rehydrate path can pick it up immediately.
        try {
          localStorage.setItem('user', JSON.stringify(user || {}));
        } catch {
          /* ignore */
        }
      }

      // Keep Redux in sync (use the values we actually stored).
      dispatch(setTenant(subdomain));
      dispatch(setCredentials({ token, user, tenantId, subdomain } as any));

      // Use replace() to avoid back-nav to the register page during tests.
      await router.replace('/dashboard');
    } catch (error: any) {
      // Normalize API errors for UI & harness:
      const status =
        error?.status ?? error?.originalStatus ?? error?.data?.status ?? error?.response?.status;
      const apiMsg =
        error?.data?.message ??
        error?.response?.data?.message ??
        (typeof error?.error === 'string' ? error.error : null);

      let msg = apiMsg || 'Registration failed';
      if (status === 409) msg = 'Email already registered';
      if (status === 400 && /password/i.test(String(apiMsg || ''))) msg = apiMsg; // surface server password validation messages
      setSubmitError(msg);
    } finally {
      submitRef.current = false;
    }
  };

  // RHF/MUI production form

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <PersonAddOutlined />
          </Avatar>
          <Typography component="h1" variant="h5">
            Create Account
          </Typography>

          {(submitError || error) && (
            <Alert severity="error" role="alert" sx={{ mt: 2, width: '100%' }}>
              {submitError ? submitError : `Registration failed: ${extractApiError(error)}`}
            </Alert>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ marginTop: 24, width: '100%' }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="companyName"
                  label="Company Name"
                  {...register('companyName')}
                  error={!!errors.companyName}
                  helperText={errors.companyName?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'companyName' }}
                />
              </Grid>
              <Grid item xs={12}>
                <ShrinkTextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  id="companyDescription"
                  label="About Your Company"
                  {...register('companyDescription')}
                  error={!!errors.companyDescription}
                  helperText={
                    (errors.companyDescription?.message as string) ||
                    'Tell customers about your company, services, and expertise (50-500 characters)'
                  }
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'companyDescription' }}
                />
              </Grid>
              <Grid item xs={12}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="subdomain"
                  label="Workspace URL"
                  {...regTap('subdomain')}
                  error={!!errors.subdomain}
                  helperText={
                    (errors.subdomain?.message as string) ||
                    'This will be your login link, e.g. https://yourchoice.visionarynow.com. Pick something short, like your company or team name.'
                  }
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'subdomain' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ShrinkTextField
                  autoComplete="given-name"
                  name="firstName"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  // E2E-safe: avoid focus race/remount only for webdriver
                  autoFocus={!IS_E2E}
                  onFocus={() => log('firstName field focused')} // H2 timeline
                  {...regTap('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'firstName' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  {...register('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'lastName' }}
                />
              </Grid>
              <Grid item xs={12}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'email' }}
                />
              </Grid>
              <Grid item xs={12}>
                <PasswordField
                  label="Password"
                  showStrengthIndicator={true}
                  autoComplete="new-password"
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message as string}
                  disabled={isLoading}
                  required
                  inputProps={{ 'data-testid': 'password' }}
                />
              </Grid>
              <Grid item xs={12}>
                <PasswordField
                  label="Confirm Password"
                  showStrengthIndicator={false}
                  autoComplete="new-password"
                  {...register('confirmPassword')}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message as string}
                  disabled={isLoading}
                  required
                  inputProps={{ 'data-testid': 'confirmPassword' }}
                />
              </Grid>
            </Grid>

            {/* Terms and Conditions Checkbox */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox {...register('termsAccepted')} color="primary" />}
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      Terms and Conditions
                    </a>
                  </Typography>
                }
              />
              {errors.termsAccepted && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errors.termsAccepted.message}
                </Typography>
              )}
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Sign Up'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component={NextLink}
                href={catalogSlug ? `/c/${catalogSlug}/login` : '/login'}
                variant="body2"
              >
                Already have an account? Sign in
              </Link>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

/**
 * New registration component with type selection
 */
const RegisterWithType: React.FC<{ catalogSlug?: string }> = ({ catalogSlug }) => {
  const [registrationType, setRegistrationType] = React.useState<'customer' | 'partner' | null>(
    null,
  );

  if (!registrationType) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
            <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
              <PersonAddOutlined />
            </Avatar>
            <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
              Create Account
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, textAlign: 'center' }}>
              Choose the type of account you want to create:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => setRegistrationType('customer')}
                  sx={{ py: 2 }}
                >
                  Customer Account
                  <Typography variant="caption" display="block">
                    Simple registration for end users
                  </Typography>
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => setRegistrationType('partner')}
                  sx={{ py: 2 }}
                >
                  Partner Account
                  <Typography variant="caption" display="block">
                    Full registration for service providers
                  </Typography>
                </Button>
              </Grid>
            </Grid>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Link
                component={NextLink}
                href={catalogSlug ? `/c/${catalogSlug}/login` : '/login'}
                variant="body2"
              >
                Already have an account? Sign in
              </Link>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return registrationType === 'customer' ? (
    <CustomerRegister catalogSlug={catalogSlug} />
  ) : (
    <RegisterRHF catalogSlug={catalogSlug} />
  );
};

/**
 * Customer registration form (simple)
 */
const CustomerRegister: React.FC<{ catalogSlug?: string }> = ({ catalogSlug }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [registerUser, { isLoading }] = useRegisterMutation();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const { checkLimit, getStatus } = useRegistrationRateLimit();

  // Check if coming from add to cart flow
  const isAddToCartFlow = router.query.action === 'add-to-cart';
  const serviceId = router.query.serviceId as string;

  const customerSchema = yup.object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    password: yup
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[^A-Za-z0-9]/, 'Password must contain at least one special character')
      .required('Password is required'),
    confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match'),
    company: yup.string().optional(),
    // Always require service address for customer accounts
    streetAddress: yup.string().required('Street address is required'),
    suite: yup.string().optional(),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    zipCode: yup.string().required('ZIP code is required'),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(customerSchema),
  });

  const onSubmit = async (data: any) => {
    try {
      setSubmitError(null);

      // Check rate limit before proceeding
      const rateLimitResult = checkLimit();
      if (!rateLimitResult.allowed) {
        const remainingTime = formatRemainingTime(rateLimitResult.remainingTime || 0);
        setSubmitError(`Too many registration attempts. Please try again in ${remainingTime}.`);
        return;
      }

      const { confirmPassword, ...rest } = data;

      const payload: any = {
        type: 'customer',
        firstName: rest.firstName,
        lastName: rest.lastName,
        email: rest.email,
        password: rest.password,
        company: rest.company || undefined,
      };

      // Always include service address for customer accounts
      payload.address = {
        street_address: rest.streetAddress,
        street_address_2: rest.suite || undefined,
        city: rest.city,
        state: rest.state,
        zip_code: rest.zipCode,
        country: 'US',
        label: 'Service Address',
        is_default: true,
      };

      // Use ApiClient for reliable test environment compatibility
      const result: any = await ApiClient.post('/auth/register', payload);

      // Handle successful registration
      const token = result?.token ?? result?.data?.token ?? '';
      const user = result?.user ?? result?.data?.user ?? {};
      const tenantId = user?.tenantId ?? user?.tenant_id ?? '';

      if (token) {
        if (typeof window !== 'undefined') {
          setToken(token);
          try {
            const secure = window.location.protocol === 'https:' ? '; Secure' : '';
            document.cookie = `${AUTH_COOKIE}=${encodeURIComponent(token)}; Path=/; SameSite=Lax; Max-Age=86400${secure}`;
          } catch {
            /* ignore */
          }
          if (tenantId) localStorage.setItem('tenantId', tenantId);
          try {
            localStorage.setItem('user', JSON.stringify(user || {}));
          } catch {
            /* ignore */
          }
        }

        dispatch(setCredentials({ token, user, tenantId } as any));

        // For catalog-specific registration, redirect to the catalog
        if (catalogSlug) {
          await router.replace(`/c/${catalogSlug}`);
        } else if (isAddToCartFlow) {
          await router.replace('/marketplace');
        } else {
          await router.replace('/dashboard');
        }
      } else {
        throw new Error('No token returned from registration');
      }
    } catch (error: any) {
      const status = error?.status ?? error?.response?.status;
      const apiMsg =
        error?.message ??
        error?.response?.data?.message ??
        (typeof error?.error === 'string' ? error.error : null);

      let msg = apiMsg || 'Registration failed';
      if (status === 409) msg = 'Email already registered';
      setSubmitError(msg);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <PersonAddOutlined />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
            {isAddToCartFlow ? 'Create Account to Add to Cart' : 'Create Customer Account'}
          </Typography>

          <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
            <Typography variant="body2" fontWeight="bold">
              🚀 Aithon Beta Access
            </Typography>
            <Typography variant="body2">
              Aithon is currently in beta and onboarding is expanding in waves.
            </Typography>
          </Alert>

          {submitError && (
            <Alert severity="error" role="alert" sx={{ mb: 2, width: '100%' }}>
              {submitError}
            </Alert>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            style={{ marginTop: 8, width: '100%' }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  {...register('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'firstName' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  {...register('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'lastName' }}
                />
              </Grid>
              <Grid item xs={12}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  type="email"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'email' }}
                />
              </Grid>
              <Grid item xs={12}>
                <ShrinkTextField
                  fullWidth
                  id="company"
                  label="Company (Optional)"
                  {...register('company')}
                  error={!!errors.company}
                  helperText={errors.company?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'company' }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                  Service Address {isAddToCartFlow ? '(Required for checkout)' : '(Required)'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {isAddToCartFlow
                    ? 'Please provide your service address to continue with adding this service to your cart.'
                    : 'Please provide your service address. This will be used as your default service location.'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="streetAddress"
                  label="Street Address"
                  {...register('streetAddress')}
                  error={!!errors.streetAddress}
                  helperText={errors.streetAddress?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'streetAddress' }}
                />
              </Grid>
              <Grid item xs={12}>
                <ShrinkTextField
                  fullWidth
                  id="suite"
                  label="Suite / Floor / Bldg (optional)"
                  {...register('suite')}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'suite' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="city"
                  label="City"
                  {...register('city')}
                  error={!!errors.city}
                  helperText={errors.city?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'city' }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      required
                      fullWidth
                      id="state"
                      label="State"
                      error={!!errors.state}
                      helperText={errors.state?.message as string}
                      disabled={isLoading}
                      inputProps={{ 'data-testid': 'state' }}
                    >
                      {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'].map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <ShrinkTextField
                  required
                  fullWidth
                  id="zipCode"
                  label="ZIP Code"
                  {...register('zipCode')}
                  error={!!errors.zipCode}
                  helperText={errors.zipCode?.message as string}
                  disabled={isLoading}
                  inputProps={{ 'data-testid': 'zipCode' }}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <PasswordField
                      label="Password"
                      value={field.value || ''}
                      onChange={field.onChange}
                      showStrengthIndicator={true}
                      autoComplete="new-password"
                      error={!!errors.password}
                      helperText={
                        (errors.password?.message as string) ||
                        'Use 8+ chars, with lowercase and a special character'
                      }
                      disabled={isLoading}
                      required
                      inputProps={{ 'data-testid': 'password' }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <PasswordField
                      label="Confirm Password"
                      value={field.value || ''}
                      onChange={field.onChange}
                      showStrengthIndicator={false}
                      autoComplete="new-password"
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message as string}
                      disabled={isLoading}
                      required
                      inputProps={{ 'data-testid': 'confirmPassword' }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={NextLink} href="/login" variant="body2">
                Already have an account? Sign in
              </Link>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

/**
 * Partner registration form (complex - uses existing RegisterRHF)
 */
const PartnerRegister: React.FC<{ catalogSlug?: string }> = ({ catalogSlug }) => {
  return <RegisterRHF catalogSlug={catalogSlug} />;
};

/**
 * Wrapper component with NO hooks.
 * Chooses between the stable E2E/Week-11 form and the customer-only registration form.
 * Partner admin registration has been moved to /registerpartner
 */
const Register: React.FC<{ catalogSlug?: string }> = ({ catalogSlug }) => {
  const IS_E2E = typeof navigator !== 'undefined' && (navigator as any).webdriver === true;
  // Strictly enable the stable registration form only during Week-11 runs.
  // Do NOT key off banner flags; Week-5 and others must use full RHF/MUI.
  const IS_W11 = process.env.SMOKE_HARNESS_WEEK === 'Week 11';
  return IS_W11 ? <W11Register /> : <CustomerRegister catalogSlug={catalogSlug} />;
};

export default Register;
// Prevent static prerender at build (avoids MUI SSG crash)
export async function getServerSideProps() {
  return { props: {} };
}
